import { Component, Element, State, getAssetPath, h } from '@stencil/core';
import state from '../../store';
import { getApiUrl } from '../../utils/enviroment';

const base = `${getApiUrl()}/wp-json/headless`;

interface FormElements extends HTMLCollection {
  descriptionInput: {
    value: string;
  };
  emailInput: {
    value: string;
  };
  telInput: {
    value: string;
  };
  termsInput: {
    checked: boolean;
  };
}

@Component({
  tag: 'hemfixarna-invoice',
  styleUrl: 'hemfixarna-invoice.scss',
  shadow: false,
})
export class HemfixarnaInvoice {
  @State() formError: string | null = null;
  @Element() private el: HTMLElement;
  @State() displayImages: Array<string | ArrayBuffer> = [];
  @State() formImages: Array<File> = [];
  @State() formState: 'initial' | 'loading' = 'initial';

  isValidEmail(email: string) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  componentDidRender() {
    const form = this.el.querySelector('form') as HTMLFormElement;
    if (form) {
      form.addEventListener('input', () => {
        this.formError = null;
      });
    }
  }

  disconnectedCallback() {
    const form = this.el.querySelector('form') as HTMLFormElement;
    if (form) {
      form.removeEventListener('input', () => {
        this.formError = null;
      });
    }
  }

  async submit(e: Event) {
    e.preventDefault();
    this.formState = 'loading';
    this.formError = null;
    const form = (e.currentTarget as HTMLFormElement).elements as FormElements;
    const values = {
      description: form.descriptionInput.value.length ? form.descriptionInput.value : null,
      email: form.emailInput.value.length ? form.emailInput.value : null,
      tel: form.telInput.value.length ? form.telInput.value : null,
      terms: form.termsInput?.checked,
    };

    if (!values.description) {
      this.formError = 'Vänligen fyll i formulärtexten';
      this.formState = 'initial';
      return;
    }
    if (!values.email || !this.isValidEmail(values.email)) {
      this.formError = 'Vänligen ange en giltig e-postadress';
      this.formState = 'initial';
      return;
    }
    if (!values.tel) {
      this.formError = 'Vänligen ange ditt telefonnummer';
      this.formState = 'initial';
      return;
    }
    if (state.selectedProduct.terms_show_checkbox && !values.terms) {
      this.formError = 'Vänligen acceptera villkoren';
      this.formState = 'initial';
      return;
    }

    const formData = new FormData();
    formData.append('text', values.description);
    formData.append('email', values.email);
    formData.append('phone', values.tel);
    formData.append('customer', state.business);

    this.formImages.forEach(file => {
      formData.append('images[]', file);
    });

    formData.append(
      'product',
      JSON.stringify({
        title: state.selectedProduct.title,
        amount: 1,
        has_rut: state.selectedProduct.rut,
        has_rot: state.selectedProduct.rot,
        has_green: state.selectedProduct.green,
        ID: state.selectedProduct.ID,
        parts: [],
      }),
    );

    try {
      const res = await fetch(`${base}/saveinvoiceproduct`, {
        method: 'POST',
        body: formData,
      });
      const { response } = await res.json();
      if (response.code === 200) {
        state.checkoutInvoice = true;
        state.step = 6;
      } else {
        this.formError = 'Vi kan inte ta emot din beställning just nu';
        this.formState = 'initial';
      }
    } catch (error) {
      this.formError = 'Vi kan inte ta emot din beställning just nu';
      this.formState = 'initial';
    }
  }

  handleImageDrop(e: DragEvent) {
    e.preventDefault();
    const files = e.dataTransfer.files;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();

        reader.onload = () => {
          this.displayImages = [...this.displayImages, reader.result];
          this.formImages = [...this.formImages, file];
        };

        reader.readAsDataURL(file);
      }
    }
  }

  handleFileInputChange(e: Event) {
    e.preventDefault();
    const files = (e.target as HTMLInputElement).files;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          this.displayImages = [...this.displayImages, reader.result];
          this.formImages = [...this.formImages, file];
        };

        reader.readAsDataURL(file);
      }
    }
  }

  handleImageClick() {
    (this.el.querySelector('.hemfixarna-file-upload') as HTMLInputElement).click();
  }

  preventDragOver(e: DragEvent) {
    e.preventDefault();
  }

  removeImage(index: number) {
    this.displayImages = this.displayImages.filter((_, i) => i !== index);
    this.formImages = this.formImages.filter((_, i) => i !== index);
  }

  render() {
    const upload = getAssetPath(`./assets/drag-drop.svg`);
    return (
      <div class="invoice">
        {state.selectedProduct?.invoice_description ? <p>{state.selectedProduct.invoice_description}</p> : null}
        <form onSubmit={e => this.submit(e)}>
          <textarea name="descriptionInput" placeholder="Beskriv ditt ärende" />
          <div role="button" aria-label="upload image" onDragOver={e => this.preventDragOver(e)} onDrop={e => this.handleImageDrop(e)} onClick={() => this.handleImageClick()}>
            <img src={upload} alt="hemfixarna_logo" width={24} />
            <div>
              <p>Bifoga ev bilder</p>
              <span>(dra bilder hit)</span>
            </div>
            <input onChange={e => this.handleFileInputChange(e)} class="hemfixarna-file-upload" accept="image/*" type="file" multiple name="fileInput" />
          </div>
          <div class="invoice-preview">
            {this.displayImages.map((img, i) => (
              <div key={i}>
                <img src={img as string} alt="uploaded image" />
                <button onClick={() => this.removeImage(i)}>
                  <img src={getAssetPath(`./assets/close.svg`)} alt="close" />
                </button>
              </div>
            ))}
          </div>
          <input placeholder="E-post" type="email" name="emailInput" />
          <input placeholder="Telefonnummer" type="tel" name="telInput" />
          {state.selectedProduct.terms_show_checkbox ? (
            <label>
              <input type="checkbox" name="termsInput" />
              {state.selectedProduct.terms}
            </label>
          ) : null}
          {this.formError ? <span>{this.formError}</span> : null}
          <input type="submit" value={this.formState === 'loading' ? 'Skickar' : 'Kontakta mig'} />
        </form>
      </div>
    );
  }
}
